import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { apiUrl, getErrorMessage } from '../utils/api';

function ChangePassword() {
  const navigate = useNavigate();
  const userInfo = useMemo(() => JSON.parse(localStorage.getItem('userInfo') || 'null'), []);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const passwordStrength = useMemo(() => {
    const value = newPassword || '';
    let score = 0;
    if (value.length >= 8) score += 1;
    if (/[A-Z]/.test(value)) score += 1;
    if (/[0-9]/.test(value)) score += 1;
    if (/[^A-Za-z0-9]/.test(value)) score += 1;
    const labels = ['Very weak', 'Weak', 'Fair', 'Good', 'Strong'];
    return {
      score,
      label: labels[Math.min(score, labels.length - 1)],
      width: `${Math.max(12, score * 25)}%`,
      tone: score >= 4 ? 'strong' : score >= 3 ? 'good' : score >= 2 ? 'fair' : 'weak',
    };
  }, [newPassword]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setMessage('');

    if (!userInfo?.token) {
      navigate('/login');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }

    setBusy(true);
    try {
      const { data } = await axios.put(
        apiUrl('/api/auth/password'),
        { oldPassword, newPassword },
        { headers: { Authorization: 'Bearer ' + userInfo.token } },
      );
      localStorage.setItem('userInfo', JSON.stringify(data));
      window.dispatchEvent(new Event('authchange'));
      setMessage('Password updated successfully.');
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      navigate('/dashboard');
    } catch (err) {
      setError(getErrorMessage(err, 'Could not update your password.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page auth-page">
      <form className="panel auth-card" onSubmit={handleSubmit}>
        <p className="eyebrow">Account security</p>
        <h1>Change password</h1>
        <p className="muted-text">Update your login password for {userInfo?.email || 'your account'}.</p>
        <input
          type="password"
          placeholder="Current password"
          value={oldPassword}
          onChange={(e) => setOldPassword(e.target.value)}
          required
        />
        <input
          type="password"
          placeholder="New password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          required
        />
        <div className="password-strength">
          <div className="password-strength-row">
            <span>Password strength</span>
            <strong>{passwordStrength.label}</strong>
          </div>
          <div className="password-strength-bar">
            <span className={`password-strength-fill ${passwordStrength.tone}`} style={{ width: passwordStrength.width }} />
          </div>
        </div>
        <input
          type="password"
          placeholder="Confirm new password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
        />
        <button type="submit" disabled={busy}>{busy ? 'Updating…' : 'Update password'}</button>
        {message && <p className="success-message">{message}</p>}
        {error && <p className="error-message">{error}</p>}
        <p className="muted-text"><Link to="/dashboard">Back to appointments</Link></p>
      </form>
    </div>
  );
}

export default ChangePassword;
